import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  Filter,
  Download,
  QrCode,
  CheckCircle,
  XCircle,
  Clock,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { cn, formatDate, formatCurrency, getStatusColor } from '@utils';
import api from '@services/api';
import type { Ticket, ListResponse } from '@types';

const statusOptions = [
  { value: 'all', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'used', label: 'Used' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'refunded', label: 'Refunded' },
];

export default function TicketsListPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const { data: tickets, isLoading } = useQuery({
    queryKey: ['tickets', { search: searchQuery, status: statusFilter, page: currentPage }],
    queryFn: async () => {
      return api.get<ListResponse<Ticket>>('/tickets', {
        search: searchQuery || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        page: currentPage,
      });
    },
  });

  const handleDownload = async (ticketId: number) => {
    try {
      const blob = await api.get<Blob>(`/tickets/${ticketId}/download`);
      const url = window.URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ticket-${ticketId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Ticket downloaded!');
    } catch (error) {
      toast.error('Failed to download ticket');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tickets</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            Manage all issued tickets and track check-ins
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button className="btn-secondary">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by ticket number, attendee, or event..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10 w-full"
          />
        </div>
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input"
            aria-label="Filter by Status"
          >
            {statusOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button className="btn-secondary">
            <Filter className="w-4 h-4" />
            Filters
          </button>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="card overflow-hidden">
        <div className="table-container">
          <table className="table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell">Ticket #</th>
                <th className="table-header-cell">Event</th>
                <th className="table-header-cell">Attendee</th>
                <th className="table-header-cell">Type</th>
                <th className="table-header-cell">Status</th>
                <th className="table-header-cell">Price</th>
                <th className="table-header-cell">Created</th>
                <th className="table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i} className="table-row">
                    {[...Array(8)].map((_, j) => (
                      <td key={j} className="table-cell">
                        <div className="h-4 bg-gray-200 rounded animate-pulse dark:bg-gray-700"></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : tickets?.data?.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-12 text-center">
                    <div className="empty-state">
                      <QrCode className="empty-state-icon" />
                      <h3 className="empty-state-title">No tickets found</h3>
                      <p className="empty-state-description">
                        Tickets will appear here once orders are placed.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                tickets?.data?.map((ticket: any, index: number) => (
                  <motion.tr
                    key={ticket.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: index * 0.05 }}
                    className="table-row"
                  >
                    <td className="table-cell font-medium">{ticket.ticketNumber}</td>
                    <td className="table-cell">{ticket.event?.name}</td>
                    <td className="table-cell">
                      <div>
                        <p className="font-medium">{ticket.attendeeName}</p>
                        <p className="text-xs text-gray-500">{ticket.attendeeEmail}</p>
                      </div>
                    </td>
                    <td className="table-cell">{ticket.ticketType?.name}</td>
                    <td className="table-cell">
                      <span className={cn('badge', getStatusColor(ticket.status))}>
                        {ticket.status}
                      </span>
                    </td>
                    <td className="table-cell">{formatCurrency(ticket.pricePaid, ticket.currency)}</td>
                    <td className="table-cell">{formatDate(ticket.createdAt)}</td>
                    <td className="table-cell">
                      <div className="flex items-center gap-2">
                        <button
                          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                          title="Download"
                          aria-label="Download Ticket"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        <button className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700" aria-label="Ticket Actions">
                          <MoreHorizontal className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {tickets?.meta && tickets.meta.lastPage > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Showing {(tickets.meta.currentPage - 1) * tickets.meta.perPage + 1} to{' '}
              {Math.min(tickets.meta.currentPage * tickets.meta.perPage, tickets.meta.total)} of{' '}
              {tickets.meta.total} tickets
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="btn-secondary p-2"
                aria-label="Previous Page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Page {currentPage} of {tickets.meta.lastPage}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(tickets.meta.lastPage, p + 1))}
                disabled={currentPage === tickets.meta.lastPage}
                className="btn-secondary p-2"
                aria-label="Next Page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
